import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ERROR_CODES, KeyValue } from '../../types/common.types';
import { ACTION_TYPES } from '../../constants/actions';
import { navigate } from 'gatsby';
import { navigation } from '../../middlewares/navigation-constants';
import { openTargetUrlAsync } from '../../helpers/common';
import { ekycActions } from '@detox/actions';

export const useEkycInit = (): void => {
  const dispatch = useDispatch();
  const { ekycState } = useSelector((state: KeyValue) => ({
    ekycState: state.ekyc
  }));
  useEffect(() => {
    const { ekycFlag, url } = ekycState?.initEkycpayload || {};
    if (ekycFlag) {
      dispatch({
        type: ACTION_TYPES.EKYC.EKYC_INITIALIZATION_SUCCESS,
        value: { initEkycpayload: null }
      });
      if (ekycFlag === 'true' && url) {
        openTargetUrlAsync(url);
      } else if (ekycFlag === 'false') {
        navigate(`/${navigation.CHECKOUT}`, { replace: true });
      }
    } else if (
      ekycState?.isInitEkycError ||
      ekycState?.initEkycpayload === ERROR_CODES.ERROR_RESPONSE
    ) {
      navigate(`/${navigation.ERROR}`, { replace: true });
    }
  }, [ekycState?.initEkycpayload, ekycState?.isInitEkycError]);
};

export const useVerifyEkyc = (): void => {
  const dispatch = useDispatch();
  const { ekycState } = useSelector((state: KeyValue) => ({
    ekycState: state.ekyc
  }));

  useEffect(() => {
    // Keep previous successful verification on remount (e.g. fulfilment -> checkout back).
    if (!ekycState?.verifyEkycPayload) {
      dispatch(ekycActions.verifyEkycWithEshop());
    }
  }, [ekycState?.verifyEkycPayload]);

  useEffect(() => {
    const { ekycFlag } = ekycState?.verifyEkycPayload || {};

    if (ekycState?.isVerifyEkycError || (ekycFlag && ekycFlag === 'true')) {
      navigate(`/${navigation.ERROR}`, { replace: true });
    }
  }, [ekycState?.verifyEkycPayload?.ekycFlag, ekycState?.isVerifyEkycError]);
};

export const useMcssVerifyEkyc = (): void => {
  const dispatch = useDispatch();
  const { ekycState } = useSelector((state: KeyValue) => ({
    ekycState: state.ekyc
  }));

  useEffect(() => {
    // Prevent duplicate dispatch on StrictMode double-mount while request is in flight.
    if (
      !ekycState?.isLoading &&
      !ekycState?.isVerifyEkycError &&
      !ekycState?.verifyEkycPayload?.customerIds
    ) {
      dispatch(ekycActions.verifyEkycWithMcss());
    }
  }, [ekycState?.isLoading, ekycState?.verifyEkycPayload?.customerIds]);

  useEffect(() => {
    //In case of NewNew, customerExists would be false
    const verifyEkycMcssPayload = ekycState?.verifyEkycPayload || {};
    const isContactExists = verifyEkycMcssPayload?.contactExists;
    if (
      ekycState?.isVerifyEkycError ||
      (verifyEkycMcssPayload?.customerIds && !isContactExists)
    ) {
      navigate(`/${navigation.ERROR}`, { replace: true });
    }
  }, [
    ekycState?.verifyEkycPayload?.contactExists,
    ekycState?.isVerifyEkycError
  ]);
};

/**
 * Used by ExistingNewFlow (authenticated/OnePass customers).
 * When not one pass logged in calls MCSS /user/customer-check (new flow).
 * When onepass loggedin: calls e-Shop verify directly (BAU — unchanged behaviour).
 */
export const useExistingCustomerVerifyEkyc = (): void => {
  const dispatch = useDispatch();
  const { ekycState, userInformation } = useSelector((state: KeyValue) => ({
    ekycState: state.ekyc,
    userInformation: state.user?.information
  }));
  const isOnePassLoggedIn = Boolean(
    userInformation && userInformation?.userDetails?.loginId
  );

  useEffect(() => {
    if (
      !ekycState?.isLoading &&
      !isOnePassLoggedIn &&
      !ekycState?.verifyEkycPayload &&
      !ekycState?.isVerifyEkycError
    ) {
      dispatch(ekycActions.verifyEkycWithMcss());
    } else if (
      !ekycState?.isLoading &&
      isOnePassLoggedIn &&
      !ekycState?.verifyEkycPayload
    ) {
      dispatch(ekycActions.verifyEkycWithEshop());
    }
  }, [ekycState?.isLoading, ekycState?.verifyEkycPayload, isOnePassLoggedIn]);

  useEffect(() => {
    if (!isOnePassLoggedIn && ekycState?.verifyEkycPayload) {
      if (
        ekycState?.isVerifyEkycError ||
        (ekycState?.verifyEkycPayload?.customerIds &&
          !ekycState?.verifyEkycPayload?.contactExists)
      ) {
        navigate(`/${navigation.ERROR}`, { replace: true });
      }
    } else if (isOnePassLoggedIn) {
      // BAU: eshop returns ekycFlag; 'true' means eKYC still required → block
      const { ekycFlag } = ekycState?.verifyEkycPayload || {};
      if (ekycState?.isVerifyEkycError || (ekycFlag && ekycFlag === 'true')) {
        navigate(`/${navigation.ERROR}`, { replace: true });
      }
    }
  }, [ekycState?.verifyEkycPayload?.ekycFlag, ekycState?.isVerifyEkycError]);
};
